// Generic imports
import { useState, useMemo, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import { useParams, useOutletContext, useNavigate, useLocation } from 'react-router-dom';

// MUI Imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import EventIcon from '@mui/icons-material/Event';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import Snackbar from '@mui/material/Snackbar';
import InputLabel from '@mui/material/InputLabel';

import {grey, green, red, purple, amber, blue, cyan} from '@mui/material/colors';

// FontAwesone Imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFloppyDisk, faCheck, faXmark, faBuilding, faCirclePlus, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { faFileSpreadsheet, faTag, faSquare, faSquareCheck, faSquareXmark, faPenToSquare, faShip, faPlane, faTruck, faPaperPlane, faDownload, faTrash, faFilterCircleDollar, faUserHeadset, faEllipsis, faClipboard } from '@fortawesome/pro-light-svg-icons';
import { faChartLineUp, faEnvelopeOpenText, faBox, faHandHoldingDollar, faEnvelopeOpenDollar, faSackDollar, faThoughtBubble } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import { ProductDescription } from '../../utils/ProductHelperComponents.js';
import Header from '../../Header.js';
import AccountsDetailForm from './AccountsDetail.form.js';
import AccountAnalytics from './AccountAnalytics.js';
import AccountEngagement from './AccountEngagement.js';
import AccountTasks from './AccountTasks.js';
import PIPELINE from './AccountFunnelPipeline.js';
import { calcAccountScore, getScoreColor, getScoreExplanation, calcOrgMaxRevenue } from '../utils/accountScore.js';
import PIPELINE_COLORS from './AccountFunnelPipeline.colors.js';
import TransportationMethod from '../../TransportationMethod.js';
import { getProductName } from '../utils/product.js';
import TradelineRow from '../tradelines/TradelineRow.js';
import TradelinePage from '../tradelines/TradelinePage.js';
import MonadaTimeline from '../timeline/Timeline.js';

function AccountsDetail(props) {
    const { mode = 'accounts', org: propsOrg } = props;
    const params = useParams();
    const context = useOutletContext();
    const { 
        accountId, tradelineId, org, seaPorts, accesstoken, me, accounts, suppliers, setAccounts, setSuppliers, 
        opportunities, featureToggle, onUpdateOpportunity, tradelines, exchangeRates, onUpdateTradeline, editable = true,
        noHeader = false,
    } = propsOrg ? props : {...context, ...params};
    const [ saving, setSaving ] = useState(false);
    const [ savingError, setSavingError ] = useState(false);
    const [ savingSuccess, setSavingSuccess ] = useState(false);
    const [ accountData, setAccountData ] = useState(null);
    const [ openReminderDialog, setOpenReminderDialog ] = useState(false);
    const [ openReminder, setOpenReminder ] = useState(null);
    const [ pipelineStep, setPipelineStep ] = useState(0);
    const [ edit, setEdit ] = useState(null);
    const [ createOpportunityDialog, setCreateOpportunityDialog ] = useState(false);
    const [ showCompleted, setShowCompleted ] = useState(false);
    const [ showPassedAccountTradelines, setShowPassedAccountTradelines ] = useState(false);
    const [ showCwUpdates, setShowCwUpdates ] = useState(false);
    const [ analyticsPeriod, setAnalyticsPeriod ] = useState('all');
    const [ openSpeedDial, setOpenSpeedDial ] = useState(false);
    const [ tmsDialogOpen, setTmsDialogOpen ] = useState(false);
    const [ tmsCreating, setTmsCreating ] = useState(false);
    const [ tmsError, setTmsError ] = useState(null);
    const [ selectedTmsCompany, setSelectedTmsCompany ] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const lastHash = useRef('');

    const _data = mode === 'accounts' ? accounts : suppliers;

    function createAsOther() {
        const newObject = {
            address: accountData.address,
            contacts: accountData.contacts,
            industry: accountData.industry,
            name: accountData.name,
            tags: accountData.tags?.filter(t => !t.label.startsWith('Pipeline: ')),
            transportationMethods: accountData.transportationMethods,
            uniqueId: accountData.uniqueId,
        };
        if (mode === 'accounts') {
            navigate(`/organization/${org.alias}/suppliers/new/details?initialData=${encodeURIComponent(btoa(JSON.stringify(newObject, null, 2)))}`);
        } else {
            navigate(`/organization/${org.alias}/accounts/new/details?initialData=${encodeURIComponent(btoa(JSON.stringify(newObject, null, 2)))}`);
        }
    }

    useEffect(() => {
        if (!org || !_data) return;

        const a = _data.find(ac => ac.id.toUpperCase() === accountId.toUpperCase());
        if (a) {
            if (!a.contacts) {
                a.contacts = [{ phone: a.phone, email: a.email }]; 
                delete a.phone; delete a.email;
            }
            setAccountData(a);
            const piplineTag = (a.tags || []).find(t => t.label.startsWith('Pipeline: '));
            setPipelineStep(PIPELINE.indexOf(piplineTag?.label?.replace('Pipeline: ', '')) || 0);
        } else {
            setAccountData({
                organizationId: org.id,
                name: '',
                contacts: [{
                    phone: '',
                    email: ''
                }]
            });
        }
    }, [org, _data, accountId]);
 
    useEffect(() => {
        if (location.hash) {
            lastHash.current = location.hash.slice(1); // safe hash for further use after navigation
        }

        setTimeout(() => {
            if (lastHash.current && document.getElementById(lastHash.current)) {
                document
                    .getElementById(lastHash.current)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                lastHash.current = '';
            }
        }, 100);
    }, [location, accountData?.updates]);

    const [ numContracts, numOneOffs, accountOpportunities ] = useMemo(() => {
        const op = (opportunities || []).filter(o => o?.account?.id === accountId && !o.deleted && o.status !== 'deleted');
        return [ op.filter(o => o.type === 'contract').length, op.filter(o => o.type === 'one-off' || o.type === 'one-off-email').length, op ];
    }, [ accountId, opportunities ]);

    // Supplier timeline: opportunities link to their buyer account, not to suppliers, so a
    // supplier's own activity (RFQs sent to them, costs of theirs used in quotes) isn't visible.
    // Scan all opportunities for this supplier in rateRequests / buyingRates and surface them as
    // timeline events. Matching mirrors addcostsmodal: prefer a real uniqueId, else supplier name,
    // else email.
    const supplierEvents = useMemo(() => {
        if (mode !== 'suppliers' || !accountData?.id) return [];
        const uid = accountData.uniqueId;
        const nameU = (accountData.name || '').trim().toUpperCase();
        const email = (accountData.email || '').toLowerCase();
        const contactEmails = new Set((accountData.contacts || []).map(c => (c?.email || '').toLowerCase()).filter(Boolean));
        const matches = (s) => {
            if (!s) return false;
            if (uid && uid.length > 3 && s.uniqueId && s.uniqueId !== 'unknown' && s.uniqueId === uid) return true;
            if (nameU && (s.organization || '').trim().toUpperCase() === nameU) return true;
            const se = (s.email || '').toLowerCase();
            return !!se && (se === email || contactEmails.has(se));
        };
        const rfqTs = (rr) => {
            const c = rr.attributes?.createdAt;
            const n = c ? (typeof c === 'number' ? c : new Date(c).getTime()) : null;
            return (n && !isNaN(n)) ? n : (rr.created || rr.modified);
        };
        const events = [];
        (opportunities || []).forEach(o => {
            if (!o || o.deleted || o.status === 'deleted') return;
            // RFQs sent to this supplier — dedupe per send (thread), keep earliest timestamp.
            const rfqs = (o.rateRequests || []).filter(rr => matches(rr.supplier));
            _.forEach(_.groupBy(rfqs, rr => rr.attributes?.conversationId || rr.attributes?.internetMessageId || rr.attributes?.pendingId || rr.id), group => {
                const ts = _.min(group.map(rfqTs).filter(Boolean));
                if (ts) events.push({ type: 'rfq-sent', timestamp: ts, opportunity: o });
            });
            // Costs of this supplier used here — dedupe per "add costs" action (shared created ts).
            const costs = (o.buyingRates || []).filter(br => matches(br.supplier));
            _.forEach(_.groupBy(costs, br => br.created || br.modified || o.modified), group => {
                const ts = Number(group[0].created || group[0].modified || o.modified);
                if (ts) events.push({ type: 'cost-added', timestamp: ts, opportunity: o, count: group.length });
            });
        });
        return events;
    }, [ mode, accountData, opportunities ]);

    const possibleTags = useMemo(() => {
        const tags = _(_data)
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
        return _.xorBy(tags, accountData?.tags, a => a.label);
    }, [_data, accountData?.tags]);

    const possibleUpdateTags = useMemo(() => {
        return _(_data)
            .map(a => a.updates?.map(u => u.tags))
            .flatten()
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
    }, [_data]);

    const accountTradelines = useMemo(() => {
        return (tradelines || []).filter(t => t.accountId === accountId);
    }, [tradelines, accountId]);

    // Get TMS companies from organization settings
    const tmsCompanies = useMemo(() => {
        let companies = _.get(org, 'integrations.cargowise.companies', []);
        if (companies.length === 0 && _.get(org, 'integrations.cargowise.companyCode')) {
            companies = [{
                companyCode: _.get(org, 'integrations.cargowise.companyCode', ''),
            }];
        }
        return companies;
    }, [org]);

    const orgMaxRevenue = useMemo(() => {
        if (!accounts || !opportunities || !exchangeRates) return 0;
        return calcOrgMaxRevenue({ accounts, opportunities: (opportunities || []).filter(o => o.status !== 'deleted'), exchangeRates, organization: org });
    }, [accounts, opportunities, exchangeRates, org]);

    const accountScore = useMemo(() => {
        if (!accountData?.id || !accountOpportunities) return null;
        return calcAccountScore({ account: accountData, opportunities: accountOpportunities, exchangeRates, orgMaxRevenue, organization: org });
    }, [accountData, accountOpportunities, exchangeRates, orgMaxRevenue, org]);

    if (tradelineId) {
        return (
            <TradelinePage />
        )
    }

    if (!accountData) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
                <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: '3px' }} />
                <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
                <Typography sx={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500 }}>Loading account...</Typography>
            </Box>
        );
    }

    function onBack() {
        navigate(-1);
    }

    function onCreate({ type, transportationMethod, routes, products } = {}) {
        const id = uuidv4();
        const defaultViewTemplate = org?.defaults?.viewTemplate || 'standard';
        onUpdateOpportunity({ opportunity: { 
            status: 'draft',
            id, 
            type,
            offers: [], 
            products: products || [],
            terms: [], 
            created: new Date().getTime(), 
            createdBy: me.email, 
            owner: me.email,
            transportationMethod,
            routes,
            account: { id: accountData.id, email: _.get(accountData, 'contacts.0.email', '') },
            ...(defaultViewTemplate !== 'standard' ? { attributes: { viewTemplate: defaultViewTemplate } } : {})
        }});
        navigate(`/organization/${org.alias}/opportunities/${id}/`);
        setCreateOpportunityDialog(false);
    }

    async function onSave({ account = accountData } = {}) {
        if (account?.address?.text && !account?.address?.id) {
            setSaving(false);
            setSavingError({ data: { detail: 'Address is incorrect. Please double check the address and fix it before saving.' } });
            await new Promise(res => setTimeout(res, 5000));
            setSavingError(null);
            return false;
        }
        setSavingError(null);
        setSavingSuccess(false);
        setSaving(true);
        account.updates = account.updates?.map(u => !u.pendingDelete ? u : {..._.omit(u, 'pendingDelete'), deleted: true });
        account.updates = account.updates?.map(u => !u.editHtml ? u : {..._.omit(u, 'editHtml', 'editTags'), html: u.editHtml, tags: u.editTags });
        account.updates = account.updates?.map(u => _.omit(u, 'wasEdited'));
        try {
            const { data } = account.id ?
                await axios.put(`/api/${mode}/${account.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { [ mode === 'accounts' ? 'account' : 'supplier' ]: _.omit(account, ['created', 'modified', 'log']) }) :
                await axios.post(`/api/${mode}/?accesstoken=${accesstoken}&organizationId=${org.id}`, { [ mode === 'accounts' ? 'account' : 'supplier' ]: account });

            if (mode === 'accounts') {
                setAccounts([..._data.filter(ac => ac.id !== account.id), data]);
            } else {
                setSuppliers([..._data.filter(ac => ac.id !== account.id), data]);
            }

            if (!account.id) {
                navigate(window.location.pathname.replace('new', data.id), { replace: true });
            }

            setSaving(false);
            setSavingSuccess(true);
            setEdit(null);
            await new Promise(res => setTimeout(res, 5000));
            setSavingSuccess(false);
            return true;
        } catch(e) {
            console.error(e);
            setSaving(false);
            setSavingError(e.response);
            await new Promise(res => setTimeout(res, 5000));
            setSavingError(null);
            return false;
        }
    }

    function onOpen({ subscreen }) {
        const newFilter = {
            days: 5000,
            taskType: subscreen,
            teamMember: 'all',
            accounts:[{label: accountData.name, ids:[accountData.id]}],
            openTasksFilter: [],
            rateRequest: 'all',
            taskStatus: 'all'
        }
        sessionStorage.setItem('FlowWelcome.main.opportunities', JSON.stringify(newFilter));
        navigate('../main');
    }

    const [ branchCode, branchName, accountUniqueId ] = (() => {
        if ((accountData.uniqueId || '').split(':').length > 1) return accountData.uniqueId.split(':');
        return ['', '', accountData.uniqueId || ''];
    })();

    async function onToggleInactive() {
        const orgInactive = accountData.inactive;
        setAccountData({ ...accountData, inactive: !orgInactive });
        const ret = await onSave({ account: { ...accountData, inactive: !orgInactive } });
        if (!ret) {
            setAccountData({ ...accountData, inactive: orgInactive });
        }
    }

    function onAddSuggestion({ suggestion }) {
        setOpenReminderDialog(true);
        setOpenReminder(suggestion);
    }

    function onEditReminder({ reminder }) {
        setOpenReminderDialog(true);
        setOpenReminder(reminder);
    }

    function onAddAccountTradeline() {
        navigate(`/organization/${org.alias}/accounts/${accountData.id}/tradelines/new`);
    }

    function onEditAccountTradeline({ tradeline, newWindow }) {
        if (newWindow) {
            window.open(`https://app.monada.ai/organization/${org.alias}/tradelines/${tradeline.id}/?accesstoken=${encodeURIComponent(accesstoken)}`, '_blank');
        } else {
            navigate(`/organization/${org.alias}/tradelines/${tradeline.id}/`);
        }
    }

    function onHideSuggestion({ suggestion }) {
        const newAccount = {
            ...accountData, 
            reminders: [
                ...accountData.reminders || [],
                { ...suggestion, status: 'rejected' }
            ] 
        };
        setAccountData(newAccount);
        onSave({ account: newAccount });
    }

    function onDeleteUpdate(update) {
        const newAccount = {
            ...accountData,
            updates: accountData.updates.map(u => u.created !== update.created ? u : {...u, pendingDelete: true})
        };
        setAccountData(newAccount);
        onSave({ account: newAccount });
    }

    async function onEditUpdateDone({ html, tags, files, prevCreated }) {
        const urls = await Promise.all(files.map(async file => {
            if (file.url) return file;
            const formData = new FormData();
            formData.append('file', file, file.name);
            const { data } = await axios({
                url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${org.id}`,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return { url: data.location, name: file.name };
        }));

        const newAccount = {
            ...accountData,
            updates: accountData.updates.map(u => u.created === prevCreated ? { ...u, editHtml: html, editTags: tags, attachments: urls } : u)
        };
        setAccountData(newAccount);
        onSave({ account: newAccount });
    }

    async function onNewUpdate({ html, tags, files, tradelineId, newTradelineName }) {
        let newTradelineCreated = null;

        if (tradelineId === 'new') {
            const tradeline = {
                title: newTradelineName,
                accountId: accountData.id,
                goals: [],
                routes: [],
            };
            const { data: newTradeline } = await axios.post(`/api/tradelines/?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline });
            tradelineId = newTradeline.id;
            newTradelineCreated = newTradeline;
        }

        const urls = await Promise.all(files.map(async file => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            const { data } = await axios({
                url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${org.id}`,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return { url: data.location, name: file.name };
        }));

        if (tradelineId) {
            const tradeline = newTradelineCreated || tradelines.find(t => t.id === tradelineId);
            if (tradeline) {
                tradeline.updates = [...(tradeline.updates || []), {
                    created: new Date().getTime(),
                    createdBy: me.email,
                    html,
                    tags,
                    attachments: urls,
                }];
                const { data: updatedTradeline } = await axios.put(`/api/tradelines/${tradelineId}?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline: _.omit(tradeline, 'log') });
                onUpdateTradeline({ tradeline: updatedTradeline, delete: false });
            }
        } else {
            const newAccountData = {
                ...accountData, updates: [...(accountData.updates || []), {
                    created: new Date().getTime(),
                    createdBy: me.email,
                    html,
                    tags,
                    attachments: urls,
                }]
            };
            setAccountData(newAccountData);
            onSave({ account: newAccountData });
        }
    }

    function onReminderOk(reminder) {
        setOpenReminderDialog(false);
        const newAccount = {
            ...accountData, 
            reminders: [
                ...((accountData.reminders || []).filter(r => r.id !== openReminder.id)),
                { ...reminder, id: openReminder.id }
            ] 
        };
        setAccountData(newAccount);
        onSave({ account: newAccount });
    }

    function onAddReminder() {
        setOpenReminder({
            id: uuidv4(),
            description: 'New Reminder',
            dueDate: new Date().toISOString()
        });
        setOpenReminderDialog(true);
    }

    function onPipelineStep(index) {
        setPipelineStep(index);
        const tags = (accountData.tags || []).filter(t => !t.label.startsWith('Pipeline: '));
        tags.push({ label: `Pipeline: ${PIPELINE[index]}` });
        setAccountData({ ...accountData, tags });
    }

    async function onSendTradelineComment({ tradeline, html, tags, files }) {
        const urls = await Promise.all(files.map(async file => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            const { data } = await axios({
                url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${org.id}`,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return { url: data.location, name: file.name };
        }));
        const newTradeline = {
            ...tradeline, updates: [...(tradeline.updates || []), {
                created: new Date().getTime(),
                createdBy: me.email,
                html,
                tags,
                attachments: urls,
            }]
        };
        const { data: updatedTradeline } = await axios.put(`/api/tradelines/${tradeline.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { tradeline: _.omit(newTradeline, 'log') });
        onUpdateTradeline({ tradeline: updatedTradeline, delete: false });
    }

    function onOpenOpportunity({ opportunity, newWindow }) {
        if (newWindow) {
            window.open(`https://app.monada.ai/organization/${org.alias}/opportunities/${opportunity.id}/?accesstoken=${encodeURIComponent(accesstoken)}`, '_blank');
        } else {
            navigate(`/organization/${org.alias}/opportunities/${opportunity.id}/`);
        }
    }

    function onOpenTradeline({ tradeline, newWindow }) {
        if (newWindow) {
            if (!location.pathname || location.pathname.length < 2) {
                window.open(`https://app.monada.ai/organization/${org.alias}/tradelines/${tradeline.id}/?accesstoken=${encodeURIComponent(accesstoken)}`, '_blank');
            } else {
                window.open(`${location.pathname.replace(/\/main/, '')}/tradelines/${tradeline.id}/?${location.search}`, '_blank');
            }
        } else {
            navigate(`/organization/${org.alias}/tradelines/${tradeline.id}/`);
        }
    }

    function onOpenTmsDialog() {
        // Validate required fields first
        if (!accountData.name || !_.trim(accountData.name)) {
            setTmsError('Missing Company Name: To create this account in your TMS, please complete the \'About this Account\' section and try again.');
            return;
        }
        if (!accountData.address || !accountData.address.text) {
            setTmsError('Missing Address: To create this account in your TMS, please complete the \'About this Account\' section and try again.');
            return;
        }

        // Set default company
        if (tmsCompanies.length > 0 && !selectedTmsCompany) {
            setSelectedTmsCompany(tmsCompanies[0].companyCode);
        }
        setTmsDialogOpen(true);
    }

    async function onCreateInTMS() {
        setTmsCreating(true);
        setTmsError(null);

        try {
            const { data } = await axios.post(
                `/api/accounts/${accountData.id}/create-in-tms?accesstoken=${accesstoken}&organizationId=${org.id}`,
                { companyCode: selectedTmsCompany || (tmsCompanies[0]?.companyCode || '') }
            );

            // Update local account data with pending status
            setAccountData({ ...accountData, tmsCreationPending: true, tmsCreationTimestamp: data.timestamp });
            setTmsDialogOpen(false);

            // Refresh accounts list
            if (setAccounts) {
                setAccounts(accounts.map(a => a.id === accountData.id ? { ...a, tmsCreationPending: true, tmsCreationTimestamp: data.timestamp } : a));
            }
        } catch (e) {
            const errorMessage = e.response?.data?.detail || 'Failed to create account in TMS. Please try again.';
            setTmsError(errorMessage);
        } finally {
            setTmsCreating(false);
        }
    }

    const handleSpeedDialOpen = () => setOpenSpeedDial(true);
    const handleSpeedDialClose = () => setOpenSpeedDial(false);

    const speedDialActions = [
        { icon: <FontAwesomeIcon icon={faEnvelopeOpenText} />, name: 'Create Opportunity: Quick Quote (Text)', onClick: () => onCreate({ type: 'one-off-free-text' }) },
        { icon: <FontAwesomeIcon icon={faEnvelopeOpenText} />, name: 'Create Opportunity: Form Builder', onClick: () => onCreate({ type: 'one-off' }) },
        { icon: <FontAwesomeIcon icon={faFileSpreadsheet} />, name: 'Create Contract', onClick: () => onCreate({ type: 'contract' }) },
        { icon: <FontAwesomeIcon icon={faChartLineUp} />, name: 'Create Sales Target', onClick: () => onAddAccountTradeline() },
    ];

    const alsoIsSupplier = mode === 'accounts' && accountData.uniqueId?.length > 3 && suppliers?.find(s => s.uniqueId === accountData.uniqueId);
    const alsoIsAccount = mode === 'suppliers' && accountData.uniqueId?.length > 3 && accounts?.find(a => a.uniqueId === accountData.uniqueId);

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            {!noHeader && (
                <Header
                    onBack={onBack}
                    icon={mode === 'accounts' ? faBuilding : faClipboard}
                    title={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
                        {accountData.name || (mode === 'accounts' ? 'New Account' : 'New Supplier')}
                        {accountScore && (() => {
                            const sc = getScoreColor(accountScore.score);
                            return (
                                <Tooltip arrow placement='bottom' title={
                                    <Box sx={{ fontSize: '10px' }}>
                                        {getScoreExplanation(accountScore).map((line, i) => (
                                            <Box key={i} sx={{ fontWeight: i === 0 ? 700 : 400, mb: i === 0 ? 0.5 : 0 }}>{line}</Box>
                                        ))}
                                    </Box>
                                }>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: '12px', background: sc.bg, border: `1px solid ${sc.border}` }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 800, color: sc.color }}>{accountScore.score}</Typography>
                                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: sc.color }}>{sc.label}</Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })()}
                    </Box>}
                    buttons={!edit && editable ? [{
                        icon: faEllipsis,
                        submenu: _.compact([mode === 'suppliers' ? null : {
                            title: `Contracts (${numContracts})`,
                            icon: faFileSpreadsheet,
                            action: () => onOpen({ subscreen: 'contract' })
                        }, mode === 'suppliers' ? null : {
                            title: `Price Quotes (${numOneOffs})`,
                            icon: faTag,
                            action: () => onOpen({ subscreen: 'one-off' })
                        }, mode === 'accounts' && featureToggle['feature.shipments'] ? {
                            title: `Shipments (0)`,
                            icon: faTruck,
                            action: () => { }
                        } : null, mode === 'accounts' && featureToggle['page.account-create-opportunity'] ? {
                            title: 'Create Opportunity',
                            icon: faEnvelopeOpenText,
                            action: () => setCreateOpportunityDialog(true),
                        } : null, mode === 'accounts' ? {
                            title: 'divider',
                        } : null, {
                            icon: mode === 'accounts' ? faClipboard : faBuilding,
                            title: mode === 'accounts' ? 'Create as Supplier' : 'Create as Account',
                            disabled: mode === ('accounts' && alsoIsSupplier) || (mode === 'suppliers' && alsoIsAccount),
                            action: () => {
                                createAsOther();
                            }
                        }])
                    }, {
                        title: 'Save',
                        icon: savingSuccess ? faCheck : savingError ? faXmark : faFloppyDisk,
                        color: savingSuccess ? 'success' : savingError ? 'error' : 'monada-button',
                        action: onSave,
                        thinking: saving,
                    }] : editable ? [{
                        title: 'Cancel',
                        icon: faXmark,
                        color: 'secondary',
                        action: () => {
                            setAccountData(edit);
                            setEdit(null);
                        },
                        disabled: saving,
                    }, {
                        title: 'Save',
                        icon: savingSuccess ? faCheck : savingError ? faXmark : faFloppyDisk,
                        color: savingSuccess ? 'success' : savingError ? 'error' : 'monada-button',
                        action: onSave,
                        thinking: saving,
                    }] : []}
                    subtitleProps={{ maxHeight: '60px', overflowY: 'auto', py: '4px' }}
                    subtitle={
                        !accountData.id ? null :
                            (
                                <Autocomplete
                                    className={'nonotch'}
                                    clearIcon={false}
                                    options={possibleTags}
                                    filterOptions={(options, state) => {
                                        const { inputValue } = state;
                                        if (inputValue.length < 1) return [];
                                        return _.sortBy(options.filter(a => a.label?.toLowerCase().startsWith(inputValue.toLowerCase())), a => a.label);
                                    }}
                                    freeSolo
                                    multiple
                                    renderTags={(value, props) =>
                                        value.map((option, index) => (
                                            <Chip size='small' label={option.label} {...props({ index })} sx={{ m: '2px', maxWidth: '180px' }} />
                                        ))
                                    }
                                    value={(accountData.tags || []).filter(t => !t.label.startsWith('Pipeline: '))}
                                    onChange={(e, value) => {
                                        const tags = value.map(a => typeof (a) === 'string' ? ({ label: a }) : a);
                                        const pipeline = (accountData.tags || []).filter(t => t.label.startsWith('Pipeline: '));
                                        setAccountData({ ...accountData, tags: [...tags, ...pipeline] });
                                    }}
                                    renderInput={(params) => !editable ? null : <TextField placeholder={editable ? 'Start typing to add tags...' : ''} sx={{ border: 0 }} InputProps={{ sx: { border: 0 } }} inputProps={{ style: { border: 0 } }} {...params} />}
                                />
                            )
                    }
                />
            )}

            {alsoIsSupplier && (
                <Typography variant='caption' component={Box} sx={{ color: grey[500], padding: '0px 24px', display: 'flex', alignItems: 'center' }}>
                    also is a <Button variant='text' color='primary' disabled={!editable} sx={{ textTransform: 'none', minWidth: '0px', padding: '0px 0px 0px 4px', fontSize: '12px !important' }} onClick={() => navigate(`../suppliers/${suppliers.find(s => s.uniqueId === accountData.uniqueId).id}/details`)}>supplier</Button>
                    .
                </Typography>
            )}

            {alsoIsAccount && (
                <Typography variant='caption' component={Box} sx={{ color: grey[500], padding: '0px 24px', display: 'flex', alignItems: 'center' }}>
                    also is an <Button variant='text' color='primary' disabled={!editable} sx={{ textTransform: 'none', minWidth: '0px', padding: '0px 0px 0px 4px', fontSize: '12px !important' }} onClick={() => navigate(`../accounts/${accounts.find(a => a.uniqueId === accountData.uniqueId).id}/details`)}>account</Button>
                    .
                </Typography>
            )}

            {accountData.inactive && (
                <Alert severity='error' fullWidth sx={{ margin: 'auto', mt: '12px', mb: 0 }}>
                    This account is inactive. It will not appear in the main list. To activate it, click the button below.
                </Alert>
            )}

            {savingError && (
                <Alert severity='error' fullWidth sx={{ margin: 'auto', mt: '12px', mb: 0 }}>
                    Error saving: {_.get(savingError, 'data.detail', 'Please try again later.')}
                </Alert>
            )}

            {accountData.id && !edit && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '24px' }}>
                    <Paper sx={{ padding: '24px', margin: '0px 24px', flex: 1 }}>
                        <Stepper activeStep={pipelineStep} nonLinear alternativeLabel>
                            {PIPELINE.map((label, index) => (
                                <Step key={label} completed={index <= pipelineStep}>
                                    <StepButton onClick={() => onPipelineStep(index)} disabled={!editable}>
                                        <StepLabel
                                            {...(index <= pipelineStep ? { StepIconComponent: () => <FontAwesomeIcon style={{ fontSize: '24px', color: PIPELINE_COLORS[index] }} icon={faCircleCheck} /> } : {})}
                                        >
                                            {label}
                                            {index === 0 && accountData.created ? <Typography variant='caption' sx={{ fontSize: '11px', color: grey[500] }} component={Box}>{dayjs(accountData.created).toString()}</Typography> : null}
                                            {index === 4 && accountData.tmsCreatedAt && (
                                                <Typography variant='caption' sx={{ fontSize: '11px', color: green[600], mt: 1 }} component={Box}>
                                                    Created in TMS on {dayjs(accountData.tmsCreatedAt).format('MMM D, YYYY')} as {accountData.branchCode || accountData.uniqueId?.split(':')[2] || 'N/A'}
                                                </Typography>
                                            )}
                                            {index === 4 && !accountData.tmsCreatedAt && _.get(org, 'integrations.cargowise.enabled', false) && (
                                                <Button
                                                    variant='outlined'
                                                    color='primary'
                                                    sx={{ textTransform: 'none', display: 'flex', alignItems: 'center', mt: 1 }}
                                                    onClick={onOpenTmsDialog}
                                                    disabled={pipelineStep !== 4 || tmsCreating || accountData.tmsCreationPending}
                                                >
                                                    {tmsCreating || accountData.tmsCreationPending ? (
                                                        <><CircularProgress size={14} sx={{ mr: 1 }} />Creating...</>
                                                    ) : (
                                                        <><FontAwesomeIcon icon={faPaperPlane} />&nbsp;Create in TMS</>
                                                    )}
                                                </Button>
                                            )}
                                        </StepLabel>
                                    </StepButton>
                                </Step>
                            ))}
                        </Stepper>
                    </Paper>
                </Box>
            )}

            {accountData.id && !edit && (
                <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Paper sx={{ padding: '24px', margin: '24px 24px 0px 24px', flex: 1, maxWidth: 'calc(100% - 48px)' }}>
                        <AccountAnalytics
                            opportunities={accountOpportunities}
                            exchangeRates={exchangeRates}
                            organization={org}
                            period={analyticsPeriod}
                            onPeriodChange={setAnalyticsPeriod}
                            updates={accountData.updates}
                            log={accountData.log}
                        />
                        <Box sx={{ borderTop: `1px solid ${grey[200]}`, mt: 2.5, pt: 2.5 }}>
                            <AccountEngagement
                                updates={accountData.updates}
                                opportunities={accountOpportunities}
                                log={accountData.log}
                                reminders={accountData.reminders}
                                period={analyticsPeriod}
                                supplierEvents={supplierEvents}
                            />
                        </Box>
                    </Paper>
                </Box>
            )}

            {accountData.id && !edit && (
                <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Paper sx={{ padding: '24px', margin: '12px 24px 0px 24px', flex: 1, maxWidth: 'calc(100% - 48px)' }}>
                        <AccountTasks
                            reminders={accountData.reminders || []}
                            setReminders={(reminders) => setAccountData({ ...accountData, reminders })}
                            onSave={(reminders) => onSave({ account: { ...accountData, reminders } })}
                            users={org.users.map(u => u.email)}
                            me={me}
                        />
                    </Paper>
                    <Paper sx={{ padding: '20px', margin: '12px 24px 0px 24px', flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                About this {mode === 'accounts' ? 'Account' : 'Supplier'}
                            </Typography>
                            {editable && (
                                <Button size='small' variant='text' color='primary' sx={{ textTransform: 'none', fontSize: '11px' }} onClick={() => setEdit(_.cloneDeep(accountData))}>
                                    <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: '10px' }} />&nbsp;Edit
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ minWidth: '150px' }}>
                                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Company</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{accountData.name}</Typography>
                            </Box>
                            <Box sx={{ minWidth: '200px' }}>
                                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Address</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{accountData.address?.text || <i style={{ color: '#94a3b8' }}>Not set</i>}</Typography>
                            </Box>
                            <Box sx={{ minWidth: '100px' }}>
                                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Owner</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{(accountData.owner || '').split('@')[0] || <i style={{ color: '#94a3b8' }}>None</i>}</Typography>
                            </Box>
                            <Box sx={{ minWidth: '100px' }}>
                                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Industry</Typography>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{accountData.industry || <i style={{ color: '#94a3b8' }}>Not set</i>}</Typography>
                            </Box>
                            {(accountData.branchCode || accountData.branchName || accountUniqueId) && (
                                <Box sx={{ minWidth: '150px' }}>
                                    <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>TMS</Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{[accountData.branchCode, accountData.branchName, accountUniqueId].filter(Boolean).join(' · ') || <i style={{ color: '#94a3b8' }}>Not set</i>}</Typography>
                                </Box>
                            )}
                            {['sea', 'air', 'land'].some(m => accountData.transportationMethods?.[m]?.active) && (
                                <Box sx={{ minWidth: '100px' }}>
                                    <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Transport</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                        {['sea', 'air', 'land'].map(m => accountData.transportationMethods?.[m]?.active ? (
                                            <FontAwesomeIcon key={m} icon={{ sea: faShip, air: faPlane, land: faTruck }[m]} style={{ fontSize: '14px', color: '#64748b' }} />
                                        ) : null)}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        {(accountData.contacts || []).length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500, mb: 0.5 }}>Contacts</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {accountData.contacts.map(c => (
                                        <Box key={c.email || c.phone} sx={{ fontSize: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', px: 1.5, py: 0.5 }}>
                                            {c.firstName || c.lastName ? <b>{`${c.firstName || ''} ${c.lastName || ''}`.trim()}</b> : null}
                                            {c.title ? <span style={{ color: '#64748b' }}> · {c.title}</span> : null}
                                            {c.email ? <span style={{ color: '#3b82f6' }}> · {c.email}</span> : null}
                                            {c.phone ? <span style={{ color: '#64748b' }}> · {c.phone}</span> : null}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}
            {mode === 'accounts' && accountData.id && !edit && (
                <Paper sx={{ padding: '24px', margin: '24px', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                                Account Targets
                            </Typography>
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ textTransform: 'none', display: 'flex', alignItems: 'center', marginLeft: '24px' }}
                                onClick={() => setShowPassedAccountTradelines(!showPassedAccountTradelines)}
                            >
                                <FontAwesomeIcon icon={showPassedAccountTradelines ? faSquareCheck : faSquare} />
                                <Box sx={{ paddingLeft: '6px' }}>
                                    Show Passed
                                </Box>
                            </Button>
                        </Box>
                        {editable && (
                            <Button variant='text' color='primary' sx={{ textTransform: 'none', display: 'flex', alignItems: 'center' }} onClick={onAddAccountTradeline}>
                                <FontAwesomeIcon icon={faCirclePlus} />&nbsp;New Account Sales Target
                            </Button>
                        )}
                    </Box>
                    <hr style={{ opacity: 0.2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1 }}>
                        {_.sortBy((accountTradelines || []).filter(r => showPassedAccountTradelines || !r.timespan || (r.timespan.end > new Date().getTime())), r => -1 * r.timespan?.end, r => -1 * r.created).map(tradeline => (
                            <TradelineRow
                                mode='row'
                                key={tradeline.id}
                                tradeline={tradeline}
                                opportunities={opportunities}
                                exchangeRates={exchangeRates}
                                onOpen={({ tradeline, newWindow }) => {
                                    onEditAccountTradeline({ tradeline, newWindow });
                                }}
                                onSendUpdate={(params) => onSendTradelineComment({ ...params, tradeline })}
                                tradelines={tradelines}
                                organization={org}
                            />
                        ))}
                    </Box>
                </Paper>
            )}
            {accountData.id && !edit && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px' }}>
                        <Typography variant='h6'>Timeline</Typography>
                        <Tooltip title={showCwUpdates ? 'CargoWise sync updates are shown' : 'CargoWise sync updates are hidden'} arrow placement='left'>
                            <Typography
                                sx={{ fontSize: '11px', color: showCwUpdates ? '#64748b' : '#94a3b8', cursor: 'pointer', '&:hover': { color: '#64748b' } }}
                                onClick={() => setShowCwUpdates(!showCwUpdates)}
                            >
                                {showCwUpdates ? '✓ CW Updates' : 'CW Updates'}
                            </Typography>
                        </Tooltip>
                    </Box>

                    <MonadaTimeline
                        updates={(accountData.updates || []).filter(u => showCwUpdates || !u.html?.includes('Account updated in Cargowise'))}
                        opportunities={accountOpportunities}
                        supplierEvents={supplierEvents}
                        log={accountData.log || []}
                        reminders={accountData.reminders || []}
                        onAddSuggestion={onAddSuggestion}
                        onHideSuggestion={onHideSuggestion}
                        onDeleteUpdate={onDeleteUpdate}
                        onEditUpdateDone={onEditUpdateDone}
                        onNewUpdate={onNewUpdate}
                        onOpenOpportunity={onOpenOpportunity}
                        onUpdateOpportunity={onUpdateOpportunity}
                        onOpenTradeline={onOpenTradeline}
                        me={me}
                        possibleUpdateTags={possibleUpdateTags}
                        featureToggle={featureToggle}
                        emails={org.users.map(u => u.email)}
                        accounts={accounts}
                        tradelines={accountTradelines || []}
                        exchangeRates={exchangeRates}
                        organization={org}
                        draftKey={`comment-draft-account-${accountId}`}
                    />

                    <Button color={!accountData.inactive ? 'error' : 'primary'} variant='text' sx={{ textTransform: 'none', marginTop: '24px', marginLeft: '20px' }} onClick={onToggleInactive}>
                        {accountData.inactive ? 'Activate Account' : 'Deactivate Account'}
                    </Button>

                    <AddReminderDialog open={openReminderDialog} setOpen={setOpenReminderDialog} initialReminder={openReminder} onOk={onReminderOk} me={me} org={org} />
                </>
            )}
            {(!accountData.id || edit) && (
                <Box sx={{ padding: '0px 24px' }}>
                    <AccountsDetailForm
                        mode={mode}
                        accounts={accounts}
                        suppliers={suppliers}
                        accountData={accountData}
                        setAccountData={setAccountData}
                        seaPorts={seaPorts}
                        me={me}
                        onSave={onSave}
                        accesstoken={accesstoken}
                        organization={org}
                    />
                </Box>
            )}

            {featureToggle['page.account-create-opportunity'] && (
                <CreateOpportunityDialog
                    account={accountData}
                    open={createOpportunityDialog}
                    onClose={() => setCreateOpportunityDialog(false)}
                    onCreate={onCreate}
                    featureToggle={featureToggle}
                />
            )}

            {accountData.id && !edit && editable && (
                <SpeedDial
                    ariaLabel="Account Actions"
                    sx={{ position: 'fixed', bottom: 16, right: 50 }}
                    icon={<SpeedDialIcon />}
                    onClose={handleSpeedDialClose}
                    onOpen={handleSpeedDialOpen}
                    open={openSpeedDial}
                >
                    {speedDialActions.map((action) => (
                        <SpeedDialAction
                            key={action.name}
                            icon={action.icon}
                            tooltipTitle={<Box sx={{ fontSize: '11px', textWrap: 'nowrap' }}>{action.name}</Box>}
                            tooltipOpen
                            onClick={() => {
                                handleSpeedDialClose();
                                action.onClick();
                            }}
                        />
                    ))}
                </SpeedDial>
            )}

            {/* TMS Creation Dialog */}
            <Dialog open={tmsDialogOpen} onClose={() => setTmsDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '8px' }} />
                    Create in TMS
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        You are about to create this account in your TMS (CargoWise). Please review the information below:
                    </DialogContentText>
                    <Box sx={{ mb: 2, p: 2, bgcolor: grey[100], borderRadius: 1 }}>
                        <Typography variant='subtitle2' color='textSecondary'>Company Name</Typography>
                        <Typography variant='body1' sx={{ fontWeight: 600 }}>{accountData.name}</Typography>
                        <Typography variant='subtitle2' color='textSecondary' sx={{ mt: 1 }}>Address</Typography>
                        <Typography variant='body1'>{accountData.address?.text || 'N/A'}</Typography>
                        <Typography variant='subtitle2' color='textSecondary' sx={{ mt: 1 }}>Owner</Typography>
                        <Typography variant='body1'>{accountData.owner || 'N/A'}</Typography>
                    </Box>
                    {tmsCompanies.length > 1 && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id='tms-company-label'>TMS Company</InputLabel>
                            <Select
                                labelId='tms-company-label'
                                value={selectedTmsCompany}
                                label='TMS Company'
                                onChange={(e) => setSelectedTmsCompany(e.target.value)}
                            >
                                {tmsCompanies.map(c => (
                                    <MenuItem key={c.companyCode} value={c.companyCode}>
                                        {c.companyCode}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTmsDialogOpen(false)} color='secondary' variant='contained' disabled={tmsCreating}>
                        Cancel
                    </Button>
                    <Button onClick={onCreateInTMS} color='primary' variant='contained' disabled={tmsCreating}>
                        {tmsCreating ? <><CircularProgress size={16} sx={{ mr: 1 }} />Creating...</> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* TMS Error Snackbar */}
            <Snackbar
                open={!!tmsError}
                autoHideDuration={8000}
                onClose={() => setTmsError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setTmsError(null)} variant='filled' sx={{ width: '100%' }}>
                    {tmsError}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AccountsDetail;

function AddReminderDialog(props) {
    const { open, setOpen, initialReminder, onOk, me, org } = props;
    const [ reminder, setReminder ] = useState({ description: '', date: '', time: '', status: 'open', assignee: me?.email || '' });

    useEffect(() => {
        if (!initialReminder) {
            setReminder({ description: '', date: '', time: '', status: 'open', assignee: me?.email || '' });
        } else {
            setReminder({
                description: initialReminder?.description || '',
                date: new Date(initialReminder?.dueDate).toISOString().split('T')[0] || '',
                time: new Date(initialReminder?.dueDate).toISOString().split('T')[1].split('.')[0] || '',
                status: initialReminder?.status || 'open',
                assignee: initialReminder?.assignee || me?.email || ''
            });
        }
    }, [ initialReminder, open ]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        onOk({
            description: reminder.description,
            dueDate: new Date(`${reminder.date}T${reminder.time}`).toISOString(),
            status: reminder.status || 'open',
            assignee: reminder.assignee || me?.email,
        });
    };

    const handleChange = (e) => {
        setReminder({ ...reminder, [e.target.name]: e.target.value });
    };

    return (
        <Dialog open={open} onClose={handleClose} aria-labelledby='form-dialog-description' sx={{ zIndex: 100000000 }}>
            <DialogTitle id='form-dialog-description' sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <EventIcon />&nbsp;Add New Reminder
                    </Box>
                    <Select
                        value={reminder.status || 'open'}
                        onChange={(e) => setReminder({ ...reminder, status: e.target.value })}
                        MenuProps={{
                            style: {
                                zIndex: 100000001,
                            },
                        }}
                    >
                        <MenuItem value={'open'}>Open</MenuItem>
                        <MenuItem value={'completed'} sx={{ color: green[500] }}><FontAwesomeIcon icon={faCheck} />&nbsp;Done</MenuItem>
                        <MenuItem value={'rejected'} sx={{ color: red[500] }}><FontAwesomeIcon icon={faXmark} />&nbsp;Canceled</MenuItem>
                    </Select>
                </Box>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    To add a new reminder, please enter the reminder description, date and time here.
                </DialogContentText>
                <TextField
                    autoFocus
                    margin='dense'
                    name='description'
                    label='Reminder Title'
                    type='text'
                    fullWidth
                    onChange={handleChange}
                    value={reminder.description}
                />
                <TextField
                    margin='dense'
                    name='date'
                    label='Reminder Date'
                    type='date'
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={handleChange}
                    value={reminder.date}
                />
                <TextField
                    margin='dense'
                    name='time'
                    label='Reminder Time'
                    type='time'
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={handleChange}
                    value={reminder.time}
                />
                {org?.users?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant='caption' sx={{ color: '#64748b' }}>Assign to</Typography>
                        <Select
                            fullWidth
                            size='small'
                            value={reminder.assignee || ''}
                            onChange={(e) => setReminder({ ...reminder, assignee: e.target.value })}
                            MenuProps={{ style: { zIndex: 100000001 } }}
                        >
                            {(org.users || []).map(u => (
                                <MenuItem key={u.email} value={u.email}>{u.email.split('@')[0]}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='secondary' variant='contained'>
                    Cancel
                </Button>
                <Button onClick={handleSave} color='primary' variant='contained' sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CreateOpportunityDialog(props) {
    const { account, open, onClose, onCreate, featureToggle } = props;

    const [ type, setType ] = useState('one-off-email');
    const [ transportationMethod, setTransportationMethod ] = useState(_.keys(account.transportationMethods)[0] || 'sea');
    const [ routes, setRoutes ] = useState([]);
    const [ products, setProducts ] = useState([]);

    useEffect(() => {
        if (open) {
            setType('one-off-email');
            setTransportationMethod(_.keys(account.transportationMethods)[0] || 'sea');
            setRoutes([]);
            setProducts([]);
        }
    }, [open]);
    
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
            <DialogTitle id='form-dialog-description' sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faEnvelopeOpenText} />&nbsp;Create Opportunity
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                <FormControl>
                    <Typography variant='body1' component={Box} sx={{ fontWeight: '700' }}>Opportunity Type</Typography>
                    <RadioGroup
                        row
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                    >
                        <FormControlLabel value='one-off-email' control={<Radio />} label='One-off' />
                        <FormControlLabel value='one-off' control={<Radio />} label='Form Builder' />
                        <FormControlLabel value='contract' control={<Radio />} label='Contract' />
                    </RadioGroup>
                </FormControl>
                <Box sx={{ padding: '3px 0px' }}>
                    <Typography variant='body1' component={Box} sx={{ fontWeight: '700' }}>Transportation Method</Typography>
                    <TransportationMethod
                        transportationMethod={transportationMethod}
                        setTransportationMethod={(v) => {
                            setTransportationMethod(v);
                            setRoutes([]);
                        }}
                        width={'100%'}
                        disabledMethods={
                            _.xor(['sea', 'air', 'land'], 
                            _.keys(account.transportationMethods).filter(t => account.transportationMethods[t]?.active))}
                    />
                </Box>
                {transportationMethod && (
                    <Box>
                        <Box sx={{ padding: '3px 0px' }}>
                            <Typography variant='body1' component={Box} sx={{ fontWeight: '700' }}>
                                Routes
                            </Typography>
                            {
                                account.transportationMethods?.[transportationMethod]?.routes?.map(route => (
                                    <Box key={JSON.stringify(route)} sx={{ paddingLeft: '12px' }}>
                                        <FormControlLabel
                                            control={<Checkbox />}
                                            label={`${route.source?.text || 'Anywhere'} → ${route.destination?.text || 'Anywhere'}`}
                                            checked={routes.includes(JSON.stringify(route))}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setRoutes([...routes, JSON.stringify(route)]);
                                                } else {
                                                    setRoutes(routes.filter(r => r !== JSON.stringify(route)));
                                                }
                                            }}
                                        />
                                    </Box>
                                ))
                            }
                        </Box>
                        <Box sx={{ padding: '3px 0px' }}>
                            <Typography variant='body1' component={Box} sx={{ fontWeight: '700' }}>
                                Products
                            </Typography>
                            {
                                account.transportationMethods?.[transportationMethod]?.products?.map(product => (
                                    <Box key={product.id} sx={{ paddingLeft: '12px' }}>
                                        <FormControlLabel
                                            control={<Checkbox />}
                                            label={getProductName({ product })}
                                            checked={products.includes(product.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setProducts([...products, product.id]);
                                                } else {
                                                    setProducts(products.filter(p => p !== product.id));
                                                }
                                            }}
                                        />
                                    </Box>
                                ))
                            }
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color='secondary' variant='contained'>
                    Cancel
                </Button>
                <Button onClick={() => onCreate({
                    type,
                    transportationMethod,
                    routes: routes.map(r => JSON.parse(r)),
                    products: products.map(p => account.transportationMethods[transportationMethod].products.find(pp => pp.id === p))
                })}
                    color='primary'
                    variant='contained'
                >
                    Create Opportunity
                </Button>
            </DialogActions>
        </Dialog>
    );
}
                    
function AccountKPIs(props) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '24px' }}>
            <Paper sx={{ padding: '24px', margin: '0px 24px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                        Account KPIs
                    </Typography>
                </Box>
                <hr style={{ opacity: 0.2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <FormLabel sx={{ marginRight: '12px' }}>
                        Calculate data from:
                    </FormLabel>
                    <Select sx={{ width: '250px' }} value={0}>
                        <MenuItem value='0'>
                            All time
                        </MenuItem>
                        <MenuItem value='1'>
                            Last 30 days
                        </MenuItem>
                        <MenuItem value='2'>
                            Last 60 days
                        </MenuItem>
                    </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <AccountSingleKPI
                        title='Quotes Created'
                        value='17'
                        caption='Total cargo: 23 containers + 1.5 ton chargeable weight'
                        icon={faEnvelopeOpenText}
                        color={purple}
                    />
                    <AccountSingleKPI
                        title='Total Shipments'
                        value='3'
                        caption='Total cargo: 3 containers + 180KG chargeable weight'
                        icon={faBox}
                        color={amber}
                    />
                    <AccountSingleKPI
                        title='Conversion Rate'
                        value='17.6%'
                        caption='Calculated 3 from 17 quotes'
                        icon={faChartLineUp}
                        color={cyan}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <AccountSingleKPI
                        title='Revenues'
                        value='$1,382,988'
                        caption='Average of $81,352 per quote'
                        icon={faHandHoldingDollar}
                        color={blue}
                    />
                    <AccountSingleKPI
                        title='Costs'
                        value='$1,106,390'
                        caption='Average of $65,079 per shipment'
                        icon={faEnvelopeOpenDollar}
                        color={red}
                    />
                    <AccountSingleKPI
                        title='Profit'
                        value='$276,598'
                        caption='20% margin'
                        icon={faSackDollar}
                        color={green}
                    />
                </Box>
            </Paper>
        </Box>
    );
}

function AccountSingleKPI(props) {
    const { title, value, caption, icon, color } = props;
    return (
        <Box sx={{ display: 'flex', padding: '24px', border: `1px solid ${grey[300]}`, borderRadius: '6px', background: 'white' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '200px' }}>
                <Typography variant='body2' component={Box} sx={{ flex: 1 }}>
                    {title}
                </Typography>
                <Typography variant='h6' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                    {value}
                </Typography>
                <Typography variant='caption' component={Box} sx={{ color: grey[500], height: '40px' }}>
                    {caption}
                </Typography>
            </Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: color[100],
                color: color[500],
                width: '50px',
                height: '50px',
                borderRadius: '12px',
                marginLeft: '12px',
                fontSize: '1.2em'
            }}>
                <FontAwesomeIcon icon={icon} />
            </Box>
        </Box>
    )
}
